import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, Phone, ShieldCheck } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

/**
 * V13.6 — Two-step phone change with OTP verification on the NEW number.
 * Backend endpoints:
 *   POST /api/users/me/change-phone           (sends OTP to new phone)
 *   POST /api/users/me/change-phone/verify    (confirms OTP, swaps phone)
 *
 * Updates `phone_verified_at` so the one-time-OTP rule continues to hold.
 */
export default function ChangePhoneDialog({ open, currentPhone, onClose, onSuccess }) {
  const [step, setStep] = useState(1);          // 1 = enter new phone, 2 = enter OTP
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [phoneMasked, setPhoneMasked] = useState('');

  const reset = () => { setStep(1); setPhone(''); setOtp(''); setPhoneMasked(''); };
  const close = () => { reset(); onClose?.(); };

  const sendOtp = async () => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) return toast.error('Enter a valid 10-digit phone');
    setBusy(true);
    try {
      const r = await api.post('/api/users/me/change-phone', {
        phone: digits,
        country_code: countryCode,
      });
      setPhoneMasked(r.data.phone_masked || `${countryCode} ${digits.slice(-4)}`);
      setStep(2);
      toast.success(`OTP sent to ${r.data.phone_masked}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = (detail && typeof detail === 'object') ? detail.message : (typeof detail === 'string' ? detail : 'Failed');
      toast.error(msg);
    }
    setBusy(false);
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) return toast.error('OTP must be 6 digits');
    setBusy(true);
    try {
      const r = await api.post('/api/users/me/change-phone/verify', { otp });
      toast.success('Phone updated. Verified.');
      onSuccess?.(r.data.phone);
      reset();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = (detail && typeof detail === 'object') ? detail.message : (typeof detail === 'string' ? detail : 'Verification failed');
      toast.error(msg);
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent data-testid="change-phone-dialog">
        <DialogHeader>
          <div className="mx-auto mb-2 h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Phone className="h-5 w-5 text-primary" />
          </div>
          <DialogTitle className="text-center">Change phone number</DialogTitle>
          <DialogDescription className="text-center">
            {step === 1
              ? `Current: ${currentPhone || '—'}. Enter the new number — we'll send an OTP to verify.`
              : `Enter the 6-digit code sent to ${phoneMasked}.`}
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-3 py-2">
            <div className="flex gap-2">
              <Input value={countryCode} onChange={(e) => setCountryCode(e.target.value)} className="w-20 text-center"
                     maxLength={4} data-testid="change-phone-cc" />
              <Input value={phone} onChange={(e) => setPhone(e.target.value)}
                     placeholder="New phone number"
                     data-testid="change-phone-new" maxLength={10} autoFocus />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={close}>Cancel</Button>
              <Button onClick={sendOtp} disabled={busy} data-testid="change-phone-send-otp">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send OTP'}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <Input
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="6-digit OTP"
              className="text-center text-lg tracking-[0.4em]"
              maxLength={6}
              autoFocus
              data-testid="change-phone-otp"
            />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={verifyOtp} disabled={busy || otp.length !== 6} data-testid="change-phone-verify">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4 mr-1" />Verify</>}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
